/**
 * <scent-canvas> — Composite hero image for the new-customer subscription PDP.
 *
 * Listens to two custom events dispatched by the MonthsSubscriptionPicker:
 *   - subscription-picker:colorChanged    → swap applicator layer
 *   - subscription-picker:scentCardUpdated → fill/clear scent slots (Task 6)
 *
 * Each event is treated as a state snapshot; the component re-derives full
 * layer state from event.detail. No diff tracking, no race conditions.
 */
class ScentCanvas extends HTMLElement {
  constructor() {
    super();
    this.selectedColor = (this.dataset.defaultColor || 'black').toLowerCase();

    this.orderedScentSkus = [];
    this.unsubs = [];
  }

  connectedCallback() {
    const subscriptionWidget = document.querySelector('subscription-picker');
    this.hasApplicator = true;
    if (subscriptionWidget?.getAttribute('data-refill-product-only') === 'true') {
      this.hasApplicator = false;
      this.setAttribute('data-has-applicator', 'false');
    }

    this.slide = this.closest('.product__media--scent-canvas');
    this.gallery = this.closest('media-gallery');
    this.dot = this.gallery?.querySelector('.product__thumbnail[data-media-id="scent-canvas"]');
    // The gallery renders a second scent canvas in the desktop sticky preview.
    // Only the canvas whose slide is actually inside the slider may control
    // carousel scroll state; the preview canvas must remain presentation-only.
    this.slider = this.slide?.closest('slider-element')
      ? this.gallery?.querySelector('slider-element[id^="SliderGallery-"]')
      : null;
    this.mediaDots = this.gallery?.querySelector('media-dots');

    this.applicatorLayers = this.querySelectorAll('.canvas-layer--applicator');
    this.scentLayers = this.querySelectorAll('.canvas-layer--scent');

    // Build SKU → layer map from each layer's own data-scent-sku. Layers are
    // rendered from the scent products themselves (each carries its own SKU),
    // so the mapping is identity-based and independent of card/layer ordering.
    this.scentSkuToLayer = new Map();
    this.scentLayers.forEach((layer) => {
      if (layer.dataset.scentSku) {
        this.scentSkuToLayer.set(layer.dataset.scentSku, layer);
      }
    });

    // Default selection order (used to preset the first N scents when the widget
    // opens) follows the picker card order.
    const scentCards = document.querySelectorAll('.scent-selector-grid .scent-card[data-scent-sku]');
    this.allOrderedSkus = scentCards.length > 0 ? Array.from(scentCards).map((c) => c.dataset.scentSku) : [];

    this.applyColor(this.selectedColor);
    this.applyScents([]);

    const onColor = (e) => this.applyColor(e?.detail?.selectedColor, e);
    const onScent = (e) => this.applyScents(e?.detail?.selectedScentSkusOrdered, e);
    const onStepChanged = () => {
      // Step changes can reveal the already-populated canvas without emitting
      // another scent event. Re-run the gallery sync so the injected canvas is
      // selected from the start of the newly visible slide set.
      if (this.slide && !this.slide.hidden) this.syncGallerySlide();
    };
    window.addEventListener('subscription-picker:colorChanged', onColor);
    window.addEventListener('subscription-picker:scentCardUpdated', onScent);
    window.addEventListener('subscription-picker:stepChanged', onStepChanged);
    this.unsubs.push(
      () => window.removeEventListener('subscription-picker:colorChanged', onColor),
      () => window.removeEventListener('subscription-picker:scentCardUpdated', onScent),
      () => window.removeEventListener('subscription-picker:stepChanged', onStepChanged),
    );
  }

  disconnectedCallback() {
    this.unsubs.forEach((fn) => fn());
    this.unsubs = [];
  }

  applyColor(color) {
    if (!color) return;
    this.selectedColor = color.toLowerCase();
    let matchingLayerFound = false;
    this.applicatorLayers.forEach((img) => {
      const matchesSelectedColor = img.dataset.color === this.selectedColor;
      matchingLayerFound ||= matchesSelectedColor;
      img.hidden = !matchesSelectedColor;
    });

    // A single-colour product may only have Shopify's "Default Title" option,
    // so it has no colour value to match against the picker's state.
    if (!matchingLayerFound && this.applicatorLayers.length === 1) {
      this.applicatorLayers[0].hidden = false;
    }
  }

  applyScents(orderedSkus = [], event) {
    // Sync layout attribute with the picker's current required quantity so
    // frequency changes (e.g. monthly→quarterly: 2→4 scents) re-flow the
    // canvas slots instead of keeping the previous layout.
    const pickerRequired = event?.target?.getRequiredScentQuantity?.();
    if (pickerRequired) {
      this.requiredScentQuantity = pickerRequired;
      this.setAttribute('required-scent-quantity', String(pickerRequired));
    }

    // Only When widget is open preset the First two scents
    if (orderedSkus.length == 0 && !!event) {
      orderedSkus = this.allOrderedSkus.slice(0, this.requiredScentQuantity || 2);
    }

    this.orderedScentSkus = orderedSkus;

    // Hide the whole canvas when no scents → gallery falls back to featured_image
    this.hidden = this.orderedScentSkus.length === 0;

    // Drive the parametric slot layout in scent-canvas.css. The host advertises
    // the actual total count; each layer advertises its own index. CSS
    // interpolates left between --slot-start and --slot-end and selects the
    // narrow 2-scent band only when the *actual* count is 2 — not when the
    // subscription minimum is 2 (one-time can grow past that).
    const slotCount = this.orderedScentSkus.length;
    this.style.setProperty('--slot-count', String(slotCount));
    if (slotCount > 0) {
      this.dataset.slotCount = String(slotCount);
    } else {
      delete this.dataset.slotCount;
    }

    // Reset state: hide all source layers, clear any clones from previous calls.
    this.scentLayers.forEach((img) => {
      img.hidden = true;
      delete img.dataset.slot;
      img.style.removeProperty('--slot-index');
    });
    this.querySelectorAll('.canvas-layer--scent-clone').forEach((c) => c.remove());

    // Place a layer for each ordered SKU. First occurrence uses the source
    // <img>; subsequent occurrences (qty > 1 of the same scent, or multiple
    // identical selections) get their own cloned <img> so the same scent
    // can occupy multiple slots simultaneously.
    const usedSources = new Set();
    this.orderedScentSkus.forEach((sku, slotIndex) => {
      const source = this.scentSkuToLayer.get(sku);
      if (!source) return;

      let layer;
      if (!usedSources.has(source)) {
        usedSources.add(source);
        layer = source;
      } else {
        layer = source.cloneNode(false);
        layer.classList.add('canvas-layer--scent-clone');
        this.appendChild(layer);
      }
      layer.hidden = false;
      layer.dataset.slot = String(slotIndex);
      layer.style.setProperty('--slot-index', String(slotIndex));
    });

    this.syncGallerySlide();
  }

  syncGallerySlide() {
    const slideVisible = !this.hidden && this.orderedScentSkus.length > 0;

    if (this.slide) {
      this.slide.hidden = !slideVisible;
    }
    if (this.dot) {
      this.dot.hidden = !slideVisible;
    }
    if (!this.slider) return;

    this.slider.reinitialize?.();
    if (this.mediaDots?.reset) this.mediaDots.reset();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (this.mediaDots?.resetIndexes) {
          this.mediaDots.resetIndexes();
        }

        this.scrollSliderToTarget(slideVisible);
      });
    });
  }

  /**
   * Scroll the gallery to the canvas slide (when visible) or the first visible
   * slide, using absolute offsets. SliderElement.select() moves relative to
   * currentPage, which is stale whenever the visible slide set has just changed
   * (step swap via data-closed CSS, slides hidden/shown), so it can land
   * mid-slide or past the end. A raw scrollLeft write also gets re-captured by
   * CSS scroll-snap, so snap/smooth are suspended for the jump — same
   * technique as SliderElement.resetScrollPosition().
   */
  scrollSliderToTarget(slideVisible) {
    const items = this.slider.itemsToShow;
    if (items.length === 0) return;

    console.log('[gallery:scent-canvas] sync:start', {
      slideVisible,
      scrollLeft: this.slider.scrollLeft,
      scrollPosition: this.slider.scrollPosition,
      currentPage: this.slider.currentPage,
      itemCount: items.length,
    });

    // The visible slide set has just changed. Wait for WebKit to commit that
    // layout before calculating offsets or writing scrollLeft.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const settledItems = this.slider.itemsToShow;
        const settledTarget = slideVisible && this.slide && !this.slide.hidden ? this.slide : settledItems[0];
        const settledIndex = Math.max(0, settledItems.indexOf(settledTarget));
        // The scent canvas is the first DOM slide by contract. Use the
        // origin explicitly rather than trusting a stale offsetLeft from the
        // previous CSS visibility set.
        const left = 0;

        this.slider.style.setProperty('scroll-behavior', 'auto', 'important');
        this.slider.style.setProperty('scroll-snap-type', 'none', 'important');
        this.slider.scrollTo({ left, behavior: 'instant' });
        this.slider.scrollLeft = left;
        this.slider.scrollPosition = left;
        this.slider.hasPendingOnScroll = false;
        this.slider.currentPage = settledIndex + 1;
        settledItems.forEach((item, i) => item.classList.toggle('selected', i === settledIndex));
        this.slider.updateButtons();

        console.log('[gallery:scent-canvas] target', {
          slideVisible,
          targetId: settledTarget?.dataset?.mediaId ?? settledTarget?.id ?? null,
          targetIndex: settledIndex,
          items: settledItems.map((item) => ({
            id: item.dataset.mediaId ?? item.id ?? null,
            hidden: item.hidden,
            width: item.clientWidth,
            offsetLeft: item.offsetLeft,
          })),
          scrollLeft: this.slider.scrollLeft,
        });

        this.slider.dispatchEvent(
          new CustomEvent('slider:change', { bubbles: true, detail: { currentPage: settledIndex + 1, currentElement: settledTarget } }),
        );

        requestAnimationFrame(() => {
        this.slider.style.removeProperty('scroll-behavior');
        this.slider.style.removeProperty('scroll-snap-type');

        // Signal only after the canvas and its slide have been injected,
        // measured, and positioned. The gallery owns the final reset so no
        // pending slider target can restore the pre-injection offset.
        this.dispatchEvent(
          new CustomEvent('scent-canvas:injected', {
            bubbles: true,
            detail: { slide: this.slide, slider: this.slider },
          }),
        );
        console.log('[gallery:scent-canvas] sync:complete', {
          scrollLeft: this.slider.scrollLeft,
          scrollPosition: this.slider.scrollPosition,
          currentPage: this.slider.currentPage,
        });
        });
      });
    });
  }
}

customElements.define('scent-canvas', ScentCanvas);
